import GameController, { GAME_EVENTS } from '../game/GameController';
import AttackController from '../attack/AttackController';
import TimeKeeper from '../engine/TimeKeeper';
import UiController from '../ui/UiController';
import { GAME_ATTACK_NAMES, GAME_ATTACK_TYPES } from '../constants/gameAttackConstants';
import AircraftModel from '../aircraft/AircraftModel';
import XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { EVENT } from '../constants/eventNames';
import EventBus from '../lib/EventBus';
import SpawnPatternCollection from '../trafficGenerator/SpawnPatternCollection';
import { REGEX } from '../constants/globalConstants';
import AirportController from '../airport/AirportController';

export const TRAFFIC_SETTINGS = {
    WIND_DIRECTION: 'windDirection',
    WIND_SPEED: 'windSpeed',
    ARRIVAL: 'arrival',
    DEPARTURE: 'departure',
    OVERFLIGHT: 'overflight',
    SPAWN_PATTERN: 'spawnPatterns',
};

export const LOCAL_STORAGE_KEY = 'local.test.data';

const GENERAL_INFORMATION_HEADING = ['UserId', 'TestId', 'Date', 'TestName', 'Airport', 'TestDuration', 'Score', 'WeightedScore'];
const EVENT_LOG_HEADING = ['TimeStamp', 'TestId', 'EventType', 'Message', 'Score', 'TotalScore'];
const TRAFFIC_SETTINGS_HEADING = ['TimeStamp', 'TestId', 'WindDirection', 'WindSpeed', 'ArrivalRate', 'DepartureRate', 'OverflightRate', 'ArrivalSpawnPatterns', 'DepartureSpawnPatterns', 'OverflightSpawnPatterns'];

class TestController{

    constructor(){

        /**
         * Local instance of the event bus
         * 
         * @type {EventBus}
         * @private
         */
        this._eventBus = EventBus;

        /**
         * The tests that are avalible to use
         * 
         * @type {Array}
         * @default []
         * @private
         */
        this.availableTests = {};

        /**
         * The location of the test data
         * 
         * @type {Object}
         * @default null
         * @private
         */
        this.testData = require('./AttackTest.json');   

        /**
         * The active test
         * 
         * @type {Object}
         * @default null
         * @private
         */
        this.test = null;

        /**
         * The id of the test so it can be identified in the database
         * 
         * @type {String}
         * @default null
         * @private
         */
        this.testId = null;

        /**
         * The status if a test is active
         * 
         * @type {Boolean}
         * @default false
         * @private
         */
        this.testIsActive = false;

        /**
         * The number of updates in the current test
         * 
         * @type {Number}
         * @default null
         * @private
         */
        this.numbOfUpdates = null;

        /**
         * The index of the next update
         * 
         * @type {Number}
         * @default 0
         * @private
         */
        this.nextUpdateIndex = 0;   
        
        /**
         * The time when the test started
         * 
         * @type {Number}
         * @default 0
         * @private
         */
        this.timeAtTestStart = 0;

        /**
         * The duration of the test
         * 
         * @type {Number}
         * @default 0
         * @private
         */
        this.testDuration = 0;

        /**
         * The time when the test will be completed
         * 
         * @type {Number}
         * @default 0
         * @private
         */
        this.testCompletionTime = 0;

        /**
         * The time of the next update
         * 
         * @type {Number}
         * @default 0
         * @private
         */
        this.timeToUpdate = 0;

        /**
         * The test log
         * 
         * @type {Object}
         * @default null
         * @private
         */
        this.TEST_LOG = null;

    }

    init (){
        this._initTestLog();
        this._loadAvailableTests();
        this.setupHandlers()
            .enable();
        // console.log(this.availableTests);
    }

    setupHandlers(){
        this.LogTrafficSettingsHandler = this.LOG_Traffic_Settings.bind(this);
        this.LogTrafficSettingChanged = this.LOG_Traffic_Setting_Change.bind(this);
        return this;
    }

    /**
     * Setups event listeners
     * 
     * @private
     * @method enable
     * @for TestController
     * @returns
     * @chainable
     */
    enable() {
        this._eventBus.on(EVENT.TRAFFIC_SETTINGS_UPDATED, this.LogTrafficSettingsHandler);
        this._eventBus.on(EVENT.TRAFFIC_SETTING_CHANGED, this.LogTrafficSettingChanged);
        return this;
    }

    disable() {
        this._eventBus.off(EVENT.TRAFFIC_SETTINGS_UPDATED, this.LogTrafficSettingsHandler);
        this._eventBus.off(EVENT.TRAFFIC_SETTING_CHANGED, this.LogTrafficSettingChanged);
        return this;
    }

    /**
     * Initializes the test log
     * 
     * @private
     * @method _initTestLog
     * @for TestController
     */
    _initTestLog(){
        this.TEST_LOG = {
            GENERAL_INFORMATION: [],
            EVENT_LOG: [],
            TRAFFIC_SETTINGS: [],
            ATTACK_SETTINGS: [],
        };
    }

    /**
     * Load the available tests from the stored test data
     * 
     * @for TestController
     * @method loadAvailableTests
     * @private
     */
    _loadAvailableTests(){
        for (const test in this.testData){
            if (this.availableTests.hasOwnProperty(test)) {
                console.warn(`A test with the name ${test} already exists. The new test will be ignored.`);
                continue;
            }
            console.log(`Test ${test} loaded`);
            this.availableTests[test] = this.testData[test];
        }
    }

    /**
     * Returns the available tests, the name and the duration
     * 
     * @returns {Array}
     * @public
     * @method getAvailableTestsNames
     * @for TestController
     */
    getAvailableTests(){
        let tests = [];
        for (const test in this.availableTests){
            tests.push({
                name: test,
                duration: this.availableTests[test].info.duration,
                level: this.availableTests[test].info.level,
                traffic: this.availableTests[test].info.traffic,
            });
        }
        return tests;
    }

    /**
     * Getter for the tests
     * 
     * @returns {Object}
     * @public
     * @method getTests
     * @for TestController
     */
    getTests(){
        return this.availableTests;
    }

    /**
     * Check that the given test exists
     * 
     * @param {String} testName
     * @private
     * @method validateTest
     * @for TestController
     */
    _validateTest(testName){
        if (!this.availableTests.hasOwnProperty(testName)) {
            console.warn(`Test ${testName} does not exist`);
            return false;
        }
        // Check that the test has the correct structure
        if (!this._validateTestStructure(this.availableTests[testName])) {
            console.warn(`Test ${testName} does not have the correct structure`);
            return false;
        }
        return true;
    }

    /**
     * Validates the structure of the given test
     * 
     * @param {Object} test
     * @private
     * @method validateTestStructure
     * @for TestController
     */
    _validateTestStructure(test){
        if (test.initparams == null) {
            console.warn('Test does not have initparams');
            return false;
        }
        if (test.updateSchema == null) {
            console.warn('Test does not have updateSchema');
            return false;
        }
        if (test.updateSchema.length === 0) {
            console.warn('Test does not have any updates');
            return false;
        }
        return true;
    }

    /**
     * Loads the given test from the available tests
     * 
     * @param {String} testName
     * @private
     * @method loadTest
     * @for TestController
     * @returns {Boolean}
     */
    _loadTest(testName) {
        if (!this._validateTest(testName)) {
            return false;
        }
        this.test = this.availableTests[testName];
        return true;
    }

    /**
     * Unloads the current test
     * 
     * @private
     * @method unloadTest
     * @for TestController
     */
    _unloadTest(){
        this.test = null;
    }

    /**
     * Extracts the number of updates from the given test
     * 
     * @param {Object} test
     * @private
     * @method extractNumberOfUpdatesFromTest
     * @for TestController
     * @returns {Number}
     */
    _extractNumberOfUpdatesFromTest(test){
        return Object.keys(test.updateSchema).length;
    }

    /**
     * Loads the initial parameters of the test
     * 
     * @private
     * @method loadInitParams
     * @for TestController
     */
    _loadInitParams(){
        //TODO: We should save the old settings and restore them after the test is done
        const initialSettings = this.test.initparams;

        // The initalsettings is a json object where the key is the settingname and value is value we want it to be
        for (const setting in initialSettings){
            AttackController.attackOptions.setAttackOptionByName(setting, initialSettings[setting]);
        }
    }

    /**
     * Sets the start values for the test
     * 
     * @private
     * @method setStartValuesForTest
     * @for TestController
     */
    _setStartValuesForTest(){
        this.numbOfUpdates = this._extractNumberOfUpdatesFromTest(this.test);
        this.timeAtTestStart = TimeKeeper.accumulatedDeltaTime;
        this.testDuration = this.test.info.duration;
        this.timeStampOfNextUpdate = this.test.updateSchema[this.nextUpdateIndex].timestamp;
        this.timeToUpdate = this.timeAtTestStart + this.timeStampOfNextUpdate;
        this.testCompletionTime = this.timeAtTestStart + this.testDuration;
    }

    /**
     * Starts a test with a given name and user id
     * 
     * @param {string} testName
     * @param {string} userId 
     */
    async startTest(testName, userId){
        if (this.testIsActive) {
            UiController.ui_log('A test is already running', true);
            console.warn('A test is already running');
            return false;
        }

        if (!this._loadTest(testName)) {
            console.warn(`Test ${testName} did not load`);
            UiController.ui_log('Test did not load', true);
            return false;
        }

        // Get test id from server, if not possible set to LOCAL_TEST
        try {
            this.testId = await this._getTestIdFromServer();
        } catch (error) {
            console.error('Failed to get test ID from server, setting to LOCAL_TEST:', error);
            this.testId = 'LOCAL_TEST';
        }

        let uiLogMessage = `Test started, ${testName}, ${userId}`;

        // Reset the attacksettings
        AttackController.attackOptions.reset();

        // Reset the traffic settings
        this._eventBus.trigger(EVENT.RESET_TRAFFIC_SETTINGS);

        // Clear the log
        this._initTestLog();

        this.testIsActive = true;
        
        uiLogMessage = `${uiLogMessage}, Test ID: ${this.testId}`;
        // Save the user and test id to GENERAL_INFORMATION
        this.TEST_LOG.GENERAL_INFORMATION.push(
            {
                "UserId": userId, 
                "TestId": this.testId, 
                "Date": new Date().toISOString(), 
                "TestName": testName,
                "Airport": AirportController.airport_get().icao, 
            }
        );
        // Set the start values for the test
        this._setStartValuesForTest();
        this._loadInitParams();

        //Restart the traffic
        this._eventBus.trigger(EVENT.TRAFFIC_RESET)

        // Clear the eventlog
        this._clearEventLog();

        // Log the traffic settings
        this._eventBus.trigger(EVENT.LOG_TRAFFIC_SETTINGS);

        // Log the attack settings
        this.LOG_Attack_Settings();

        // Test is running
        // ...
        // Update the settings when needed
        // ...
        // Log the remaing GENRAL_INFORMATION
        // This is done in endtest

        // Save the test to local storage
        // This is done in endtest

        // Send the test to the server
        // This will be done in endtest

        // Show the feedback window
        // This is done in endtest
        
        UiController.ui_log(uiLogMessage, true);

        console.log(`Start test at time: ${this.timeAtTestStart.toFixed(0)}`);
        console.log(`Next update at time: ${this.timeToUpdate.toFixed(0)}`);
        console.log(`Test will be done at time: ${this.testCompletionTime.toFixed(0)}`);
        console.log(`The duration of the test is ${this.testDuration}`);

        return true;
    }

    /**
     * Ends the current test
     * 
     * @public
     * @method endTest
     * @for TestController
     */
    endTest(){
        if (!this.testIsActive) {
            UiController.ui_log(
                "Cannot stop test because no test is currently running.",
                true
            );
            console.warn("No test is currently running.");
            return false;
        }
        // Mark all attacked aircraft as no guess before ending the test
        const attackedAircraft = AttackController.getAttackedAircraft();

        const airport = AirportController.airport_get();
        attackedAircraft.forEach((aircraft) => {
            if (aircraft.isInsideAirspace(airport))
                AttackController.markNoGuessForAircraft(aircraft);
        });

        this.testIsActive = false;

        const totalScore = GameController.game_get_Accumulated_Score();
        const weightedGameScore = GameController.game_get_weighted_score().toFixed(0);
        const runningTime = (TimeKeeper.accumulatedDeltaTime - this.timeAtTestStart).toFixed(0);

        console.log(`Test ended at time: ${TimeKeeper.accumulatedDeltaTime.toFixed(0)}`);
        console.log(`The test running time was: ${runningTime}`);

        this.TEST_LOG.GENERAL_INFORMATION[0]['Score'] = totalScore;
        this.TEST_LOG.GENERAL_INFORMATION[0]['WeightedScore'] = weightedGameScore;
        this.TEST_LOG.GENERAL_INFORMATION[0]['TestDuration'] = runningTime;

        this._resetToDefaultValues();
        this._unloadTest();

        // Store the test log localy in the browser
        this._storeTestDataToLocal(this.TEST_LOG);

        // Send the test to the server
        this._sendTestToServer(this.TEST_LOG);

        // Show the feedback window
        UiController.testFeedbackUiController.toggleDialog();
    }

    /**
     * Sends the test to the server
     * 
     * @param {Object} testlog
     * @private
     */
    _sendTestToServer(testlog) {
        fetch('/api/data', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(testlog),
        }).catch((error) => {
            console.error('There was a problem submitting the testlog:', error);
        });
    }

    /**
     * Resets the settings to the default settings
     * 
     * @private
     * @method resetSettings
     * @for TestController
     */
    _resetToDefaultValues(){
        this.numbOfUpdates = 0;
        this.timeAtTestStart = 0;
        this.testDuration = 0;
        this.timeStampOfNextUpdate = 0;
        this.timeToUpdate = 0;
        this.testCompletionTime = 0;
        this.nextUpdateIndex = 0;
    }

    _storeTestDataToLocal(newTestData){   
        let storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
    
        let testDataArray;
        if (storedData) {
            try {
                testDataArray = JSON.parse(storedData);
            } catch (e) {
                console.error('Error parsing stored data:', e);
                testDataArray = [];
            }
        } else {
            testDataArray = [];
        }
    
        testDataArray.push(newTestData);
    
        try {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(testDataArray));
        } catch (e) {
            console.error('Error storing data:', e);
        }
    }

    updateSettings(){
        if (!this.testIsActive) {
            return;
        }
        const currentTime = TimeKeeper.accumulatedDeltaTime;
        const timeToUpdate = this.timeToUpdate;
        const updateIndex = this.nextUpdateIndex;

        //console.log(currentTime);

        if(currentTime > timeToUpdate){

            if(currentTime > this.testCompletionTime){
                this.endTest();
                return;
            }

            this.directInjectAircraft(updateIndex);
            this.injectAircraft(updateIndex);
            this.changeTrafficSettings(updateIndex); 
            this.changeAttackSettings(updateIndex);   
            this.updateIndexAndTimeout();

            console.log(`Settings updated at ${currentTime.toFixed(0)}`);
        }

    }

    /**
     * Updated the index checks if the test is over
     * 
     * @private
     */
    updateIndexAndTimeout(){
        this.nextUpdateIndex++;

        if (this.nextUpdateIndex < this.numbOfUpdates) {
            this.timeToUpdate = this.timeAtTestStart + this.test.updateSchema[this.nextUpdateIndex].timestamp;
        } else {
            this.timeToUpdate = this.testCompletionTime;
        }
    }

    isTestActive(){
        return this.testIsActive;
    }

    /**
     * Changes the attack settings given from the test file
     * 
     * @param {Number} updateIndex
     * @private
     */
    changeAttackSettings(updateIndex){
        const settings = this.test.updateSchema[updateIndex].attackSettings;

        if (!settings) {
            return;
        }

        for(const setting in settings){
            AttackController.attackOptions.setAttackOptionByName(setting, settings[setting]);
            console.log(`Setting: ${setting} changed to: ${settings[setting]}`);
        }
    }

    /**
     * Changes the traffic settings given from the test file
     * 
     * @param {Number} updateIndex
     * @private
     * @method changeTrafficSettings
     * @for TestController
     */
    changeTrafficSettings(updateIndex){
        const settings = this.test.updateSchema[updateIndex].trafficSettings;

        if (!settings) {
            return;
        }

        for(const setting in settings) {
            switch(setting) {
                case TRAFFIC_SETTINGS.WIND_DIRECTION:
                    this._eventBus.trigger(EVENT.SET_WIND_DIRECTION, settings[setting]);
                    break;
                case TRAFFIC_SETTINGS.WIND_SPEED:
                    this._eventBus.trigger(EVENT.SET_WIND_SPEED, settings[setting]);
                    break;
                case TRAFFIC_SETTINGS.ARRIVAL:
                    this._eventBus.trigger(EVENT.SET_ARRIVAL_TRAFFIC_RATE, settings[setting]);
                    break;
                case TRAFFIC_SETTINGS.DEPARTURE:
                    this._eventBus.trigger(EVENT.SET_DEPARTURE_TRAFFIC_RATE, settings[setting]);
                    break;
                case TRAFFIC_SETTINGS.OVERFLIGHT:
                    this._eventBus.trigger(EVENT.SET_OVERFLIGHT_TRAFFIC_RATE, settings[setting]);
                    break;
                default:
                    console.warn(`Unknown traffic setting: ${setting}`);
            }
            console.log(`Setting: ${setting} changed to: ${settings[setting]}`);
        }
    }
    
    /**
     * Directly injects an attacked aircraft into the simulation
     * 
     * @param {number} updateIndex 
     * @private
     */
    directInjectAircraft(updateIndex) {
        const attackTypes = this.test.updateSchema[updateIndex].directInjection;

        if (!attackTypes) {
            return;
        }

        for (let i = 0; i < attackTypes.length; i++) {
            const attackType = attackTypes[i];
            if (GAME_ATTACK_TYPES.hasOwnProperty(attackType)) {
                const attackNum = GAME_ATTACK_TYPES[attackType];
                AttackController.directInjectAttackedAircraft(attackNum);
                console.log(`Directly injected attack type: ${attackType}.`);
            } else {
                console.warn(`Unknown attack type: ${attackType}`);
            }
        }
    }

    /**
     * Injecects an attacked aircraft into the simulation spawn scheduler
     * 
     * @param {Number} updateIndex
     * @private
     */
    injectAircraft(updateIndex){
        const attackTypes = this.test.updateSchema[updateIndex].directInjection;

        if (!attackTypes) {
            return;
        }

        for (let i = 0; i < attackTypes.length; i++) {
            const attackType = attackTypes[i];
            if (GAME_ATTACK_TYPES.hasOwnProperty(attackType)) {
                const attackNum = GAME_ATTACK_TYPES[attackType];
                AttackController.addAttackToQueue(attackNum);
                console.log(`Directly injected attack type: ${attackType}.`);
            } else {
                console.warn(`Unknown attack type: ${attackType}`);
            }
        }
    }

    /**
     * Clears the eventlog
     * 
     * @private
     */
    _clearEventLog(){
        this.TEST_LOG.EVENT_LOG = [];
    }

    /**
     * Helper function to convert a string to an array buffer, used for creating the xlsx file
     * 
     * @param {Object} s 
     * @returns 
     */
    s2ab(s) {
        const buf = new ArrayBuffer(s.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xFF;
        return buf;
    }

    /**
     * Downloads the test log
     * 
     * @public
     * @method downloadTestLog
     * @for TestController
     */
    downloadTestLog(testlog = null){
        let logToDownload = this.TEST_LOG;
        if (testlog) {
            logToDownload = testlog;
        }

        let wb = XLSX.utils.book_new();

        // Create a sheet with a summary of the scores
        let ws = XLSX.utils.json_to_sheet(logToDownload.GENERAL_INFORMATION, {header: GENERAL_INFORMATION_HEADING});
        XLSX.utils.book_append_sheet(wb, ws, 'GeneralInformation');

        ws = XLSX.utils.json_to_sheet(logToDownload.EVENT_LOG, {header: EVENT_LOG_HEADING});
        XLSX.utils.book_append_sheet(wb, ws, 'EventLog');

        ws = XLSX.utils.json_to_sheet(logToDownload.TRAFFIC_SETTINGS, {header: TRAFFIC_SETTINGS_HEADING});
        XLSX.utils.book_append_sheet(wb, ws, 'TrafficSettings');

        const ATTACK_SETTINGS_HEADING = this._buildAttackSettingsHeading();
        ws = XLSX.utils.json_to_sheet(logToDownload.ATTACK_SETTINGS, {header: ATTACK_SETTINGS_HEADING});
        XLSX.utils.book_append_sheet(wb, ws, 'AttackSettings');

        let wbBinaryString = XLSX.write(wb, { type: 'binary', bookType: 'xlsx' });
        let blob = new Blob([this.s2ab(wbBinaryString)], { type: 'application/octet-stream' });

        console.log("xlsx");

        // Save Blob using file-saver
        saveAs(blob, 'TestLog.xlsx');
    }

    /**
     * Download the test log as a json file
     * 
     * @public
     * @method downloadTestLogAsJson
     * @for TestController
     */
    downloadTestLogAsJson(){
        const copyOfTestLog = this.TEST_LOG;

        // // Add the headings to the start of the log
        // this._buildHeadingsForLogJson(copyOfTestLog);

        var element = document.createElement('a');
        element.setAttribute('href', 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(copyOfTestLog, null, 1)));
        element.setAttribute('download', "log.json");
  
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    }

    /**
     * Gets a unique test id from the server that will be used to identify the test
     * 
     * @private
     * @returns testid
     */
    async _getTestIdFromServer(){
        try {
            const response = await fetch('/api/testId');
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            return data.testId;
        } catch (error) {
            console.error('There was a problem with the fetch operation:', error);
            throw error;
        }
    }

    /**
     * Submits the feedback to the server
     * 
     * @param {string} feedback
     * @public
     * @method submitFeedback
     * @for TestController
     */
    submitFeedback(feedback) {
        this._sendFeedbackToServer(feedback, this.testId);
    }

    /**
     * Sends the feedback to the server
     * 
     * @param {string} feedback
     * @param {string} testId
     */
    _sendFeedbackToServer(feedback, testId) {
        try {
            fetch('/api/feedback', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ feedback, testId }),
            }).then(response => response.json())
            .then(data => console.log('Success:', data))
            .catch(error => console.error('Error:', error));
        } catch (error) {
            console.error('There was a problem submitting the feedback:', error);
        }
    }

    /**
     * Builds the headings for the log json, and appends them to the start.
     * 
     * @private
     * @method _buildHeadingsForLogJson
     * @for TestController
     */ 
    _buildHeadingsForLogJson(log) {
        log.EVENT_LOG.unshift(EVENT_LOG_HEADING);
        log.GENERAL_INFORMATION.unshift(GENERAL_INFORMATION_HEADING);
        log.TRAFFIC_SETTINGS.unshift(TRAFFIC_SETTINGS_HEADING);
        const attackSettings = this._buildAttackSettingsHeading();
        log.ATTACK_SETTINGS.unshift(attackSettings);
    }

    /**
     * Returns a array with the headings for the attack settings
     * 
     * @returns [string]
     */
    _buildAttackSettingsHeading(){
        let attackSettings = ['TimeStamp', 'TestId'];
        const attacks = AttackController.attackOptions.getAttackOptionsNames();
        for (let attack in attacks) {
            attackSettings.push(attacks[attack]);
        }
        return attackSettings;
    }

    /**
     * Returns the stored testlogs from the local storage
     * 
     * @returns [testlogs]
     */
    getStoredTestData(){
        let storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (storedData) {
            try {
                return JSON.parse(storedData);
            } catch (e) {
                console.error('Error parsing stored data:', e);
                return [];
            }
        } else {
            return [];
        }
    }

    /**
     * Clears the local storage
     * 
     * @public
     * @method clearLocalStorage
     * @for TestController
     */
    clearLocalStorage() {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
    }

    /**
     * Logs game events to the test log
     * 
     * @param {string} event 
     * @param {number} eventPoints 
     * @param  {...any} args 
     * @returns 
     */
    LOG_New_Game(event, eventPoints, ...args) {
        if (!this.testIsActive) {
            return;
        }

        const timeStamp = TimeKeeper.accumulatedDeltaTime.toFixed(1);
        let message = event;   
        
        switch(event){
            case GAME_EVENTS.NO_POINTS_FOR_GUESS:
            case GAME_EVENTS.NO_DEDUCTION_FOR_GUESS:
            case GAME_EVENTS.NO_POINTS_FOR_INCORRECT_GUESS:
            case GAME_EVENTS.ONE_MINUTE_CORRECT_GUESS:
            case GAME_EVENTS.TWO_MINUTE_CORRECT_GUESS:
            case GAME_EVENTS.THREE_MINUTE_CORRECT_GUESS:
            case GAME_EVENTS.FOUR_MINUTE_CORRECT_GUESS:
            case GAME_EVENTS.FIVE_MINUTE_CORRECT_GUESS:
            case GAME_EVENTS.FIVE_MINUTE_PLUS_CORRECT_GUESS:
            case GAME_EVENTS.INCORRECT_GUESS:
            case GAME_EVENTS.DEDUCT_ONE_MINUTE_CORRECT_GUESS:
            case GAME_EVENTS.DEDUCT_TWO_MINUTE_CORRECT_GUESS:
            case GAME_EVENTS.DEDUCT_THREE_MINUTE_CORRECT_GUESS:
            case GAME_EVENTS.DEDUCT_FOUR_MINUTE_CORRECT_GUESS:
            case GAME_EVENTS.DEDUCT_FIVE_MINUTE_CORRECT_GUESS:
            case GAME_EVENTS.DEDUCT_FIVE_MINUTE_PLUS_CORRECT_GUESS: {
                const gueesType = args[0];
                const aircraft = args[1];
                const callsign = aircraft.callsign;
                const aircraftId = aircraft.id;
                switch (gueesType) {
                    case 'RESET_GUESS_FROM_CORRECT': {
                        const oldGuess = args[2];
                        message = `Reset guess on ${aircraftId} (${callsign}) from correct guess of ${GAME_ATTACK_NAMES[oldGuess].name[0]}.`;	
                        break;
                    }
                    case 'CORRECT_GUESS': {
                        const guess = args[2];
                        message = `Correct guess on ${aircraftId} (${callsign}) with ${GAME_ATTACK_NAMES[guess].name[0]}.`;
                        break;
                    }
                    case 'INCORRECT_GUESS': {
                        const guess = args[2];
                        message = `Incorrect guess of ${GAME_ATTACK_NAMES[guess].name[0]} on ${aircraftId} (${callsign}) with ${GAME_ATTACK_NAMES[aircraft.attackType].name[0]}.`;
                        break;
                    }
                    case 'INCORRECT_GUESS_FROM_CORRECT': {
                        const guess = args[2];
                        message = `The guess (${GAME_ATTACK_NAMES[guess].name[0]}) was changed from correct to incorrect on ${aircraftId} (${callsign}) with ${GAME_ATTACK_NAMES[aircraft.attackType].name[0]}.`;
                        break;
                    }
                }
                break;
            }
            case GAME_EVENTS.NO_GUESS: {
                const aircraft = args[0];
                const callsign = aircraft.callsign;
                const aircraftId = aircraft.id;
                const attackType = aircraft.attackType;
                message = `No guess was made on ${aircraftId} (${callsign}) before the aircraft was removed, or the test ended. It was attacked with ${attackType}.`;
                break;
            }
            case GAME_EVENTS.ATTACK_SETTING_UPDATE: {
                const setting = args[0];
                const oldValue = args[1];
                const newValue = args[2];
                message = `The attack setting ${setting} was changed from ${oldValue} to ${newValue}.`;
                break;
            }
                
        }
        
        const entry = this.eventLogEntryBuilder(event, message, eventPoints);
        this.TEST_LOG.EVENT_LOG.push(entry);
        console.log(`TimeStamp: ${timeStamp}, ${event}`);
    }

    /**
     * Logs the messages in the ui console
     * 
     * @param {string} message 
     * @returns 
     */
    LOG_New_UI_LOG_ENTRY(message){
        if (!this.testIsActive) {
            return;
        }
        const timeStamp = TimeKeeper.accumulatedDeltaTime.toFixed(1);
        const EVENT_TYPE = 'UI_LOG';
        const entry = this.eventLogEntryBuilder(EVENT_TYPE, message, 0);
        this.TEST_LOG.EVENT_LOG.push(entry);
        console.log(`TimeStamp: ${timeStamp}, ${message}`);
    }

    /**
     * Logs the command sent int the console
     * 
     * @param {AircraftModel} aircaft 
     * @param {string} command 
     * @param {Object} data 
     * @returns 
     */
    LOG_New_Command(aircaft, command, data){
        if (!this.testIsActive) {
            return;
        }
        const attackType = aircaft.attackType;
        const callsign = aircaft.callsign;
        const aircraftId = aircaft.id;
        const attackName = GAME_ATTACK_NAMES[attackType].name[0];

        const message = `Command: ${command} with data: ${data} on aircraft with id: ${aircraftId}, (${callsign}), attack type: ${attackName}`;

        const entry = this.eventLogEntryBuilder('COMMAND', message, 0);

        this.TEST_LOG.EVENT_LOG.push(entry);
    }

    /**
     * Logs that a new aircraft with an attack has been added
     * 
     * @param {AircraftModel} aircraft 
     */
    LOG_New_Attacked_Aircraft(aircraft){
        if (!this.testIsActive) {
            return;
        }

        const callsign = aircraft.callsign;
        const aircraftId = aircraft.id;
        const attackType = aircraft.attackType;
        const attackName = GAME_ATTACK_NAMES[attackType].name[0];

        const message = `New attacked aircraft with id: ${aircraftId}, (${callsign}), attack type: ${attackName} added.`;

        const entry = this.eventLogEntryBuilder('NEW_ATTACKED_AIRCRAFT', message, 0);

        this.TEST_LOG.EVENT_LOG.push(entry);
    }

    /**
     * Logs the current attack settings to the test log
     * 
     * @public
     * @method LOG_Attack_Settings
     * @for TestController
     * @returns {Array}
     */
    LOG_Attack_Settings(){
        if (!this.testIsActive) {
            return;
        }
        const timeStamp = TimeKeeper.accumulatedDeltaTime.toFixed(1);

        let settings = {
            "TimeStamp": timeStamp, 
            "TestId": this.testId
        };

        const optionNames = AttackController.attackOptions.getAttackOptionsNames();

        for (let optionName of optionNames) { 
            const option = AttackController.attackOptions.getAttackOptionByName(optionName);
            settings[optionName] = option;
        }

        this.TEST_LOG.ATTACK_SETTINGS.push(settings);
        console.log(`TimeStamp: ${timeStamp}, Attack settings changed`);
    }

    /**
     * Logs the current traffic settings to the test log, this i called with event bus from the traffic controller when a setting is changed
     * 
     * @public
     * @returns null
     */
    LOG_Traffic_Settings(...args){
        if (!this.testIsActive) {
            return;
        }

        const timeStamp = TimeKeeper.accumulatedDeltaTime.toFixed(1);
        const windDirection = args[0];
        const windSpeed = args[1];
        const arrival = args[2];
        const departure = args[3];
        const overflight = args[4];
        const arrivalSpawnPatterns = args[5];
        const departureSpawnPatterns = args[6];
        const overflightSpawnPatterns = args[7];

        const settings = {
            "TimeStamp": timeStamp, 
            "TestId": this.testId, 
            "WindDirection": windDirection, 
            "WindSpeed": windSpeed, 
            "ArrivalRate": arrival, 
            "DepartureRate": departure, 
            "OverflightRate": overflight, 
            "ArrivalSpawnPatterns": arrivalSpawnPatterns, 
            "DepartureSpawnPatterns": departureSpawnPatterns, 
            "OverflightSpawnPatterns": overflightSpawnPatterns
        };

        this.TEST_LOG.TRAFFIC_SETTINGS.push(settings);
        console.log(`TimeStamp: ${timeStamp}, Traffic settings changed`);
    }

    /**
     * Logs a specific traffic setting change, this is called with a
     * 
     * @param  {...any} args 
     * @returns 
     */
    LOG_Traffic_Setting_Change(...args){
        if (!this.testIsActive) {
            return;
        }

        const setting = args[0];
        let oldValue = null;
        let newValue = null;
        let spawnPatternId = null;
        let message = '';

        switch(setting){
            case TRAFFIC_SETTINGS.WIND_DIRECTION:
            case TRAFFIC_SETTINGS.WIND_SPEED:
            case TRAFFIC_SETTINGS.ARRIVAL:
            case TRAFFIC_SETTINGS.DEPARTURE:
            case TRAFFIC_SETTINGS.OVERFLIGHT:
                oldValue = args[1];
                newValue = args[2];
                message = `The traffic setting ${setting} was changed from ${oldValue} to ${newValue}.`;
                break;
            case TRAFFIC_SETTINGS.SPAWN_PATTERN:
                spawnPatternId = args[1];
                oldValue = args[2];
                newValue = args[3];
                let spawnPatternRouteString = SpawnPatternCollection.findSpawnPatternById(spawnPatternId).routeString.replace(REGEX.SINGLE_DOT, ' ');
                message = `The traffic setting for spawn pattern ${spawnPatternRouteString} was changed from ${oldValue} to ${newValue}.`;
                break;
        }

        if (message === '') {
            return;
        }

        const entry = this.eventLogEntryBuilder('TRAFFIC_SETTING_CHANGE', message, 0);

        this.TEST_LOG.EVENT_LOG.push(entry);
    }

    /**
     * Builds the eventlog entry
     * 
     * @param {string} eventType 
     * @param {string} message 
     * @param {number} eventPoints 
     * @returns 
     */
    eventLogEntryBuilder(eventType, message, eventPoints){
        const timeStamp = TimeKeeper.accumulatedDeltaTime.toFixed(1);
        const totalScore = GameController.game_get_Accumulated_Score();

        return {
            "TimeStamp": timeStamp, 
            "TestId": this.testId, 
            "EventType": eventType, 
            "Message": message, 
            "Score": eventPoints, 
            "TotalScore": totalScore
        };
    }

}

export default new TestController();